import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, CircularProgress, Grid, Card, CardContent, Button, TextField } from '@mui/material';
import api from '../api';
import { useDemo } from '../demoMode';

// [D-734] OWNER 25.10: «бачити хто кому передав посилання на мої застосунки… я надам посилання своїм партнерам, а вони будуть
// надавати їх своїм учням… при першому запуску застосунка я хочу отримати сповіщення і мати в себе в дашборді окреме меню
// хто кого привів». Дані - /api/app-invites (app_invites.py): особисті посилання, натиски, перший запуск, підключення.
const METHOD = {
  link:     { t: '🔗 за посиланням', c: '#4caf50' },
  self:     { t: '📲 сам, кнопкою в Студії', c: '#f472b6' },   // [D-736] своє посилання = кнопка «📲 Застосунок»; привів - партнер / ви
  partner:  { t: '🤝 учень партнера', c: '#4fc3f7' },
  referral: { t: '🎟 запросив у бота', c: '#ffb74d' },
  none:     { t: '❔ невідомо', c: '#9e9e9e' },
};
const PF = { phone: { t: '📱 Телефон', c: '#f472b6' }, pc: { t: '💻 ПК', c: '#22d3ee' } };
const EV = { open: '👀 відкрили посилання', android: '📱 натиснули «Android»', pc: '💻 натиснули «Windows»' };
const kyiv = (iso) => iso ? new Date(iso).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
const uname = (i) => i.telegram_username ? '@' + i.telegram_username : 'ID ' + (i.telegram_id || i.user_id);
const chipSx = (c) => ({ bgcolor: c + '22', color: c, border: '1px solid ' + c + '66', fontWeight: 'bold' });

export default function AppInvitesPage() {
  const demo = useDemo();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('tree');
  const [uid, setUid] = useState('');
  const [msg, setMsg] = useState('');

  const [rev, setRev] = useState(0);   // +1 - перечитати одразу (після нового посилання)
  useEffect(() => {
    // 🔴 у демо - нічого справжнього: імена учнів і посилання не для сторонніх очей
    if (demo) { setData({ links: [], installs: [], events: [] }); return undefined; }
    const load = () => api.get('/app-invites').then(r => setData(r.data)).catch(() => setData({ links: [], installs: [], events: [] }));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [demo, rev]);

  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }}/></Box>;

  const links = data.links || [], installs = data.installs || [], events = data.events || [];
  const byCode = Object.fromEntries(links.map(l => [l.code, l]));
  const keyOf = (kind, id) => kind === 'owner' ? 'owner' : kind + ':' + id;
  const linkName = (l) => !l ? '?' : l.kind === 'owner' ? '👑 Ви' : l.kind === 'partner' ? '🤝 ' + l.name : '👤 ' + l.name;

  // юзери, що підключили застосунок: усі їхні пристрої разом; батько - той, хто привів
  const users = {};
  installs.filter(i => i.user_id).forEach(i => {
    const u = users[i.user_id] = users[i.user_id] || { ...i, pf: {}, first: i.paired_at, parent: i.via_kind ? keyOf(i.via_kind, i.via_id) : 'none' };
    u.pf[i.platform] = true;
    if (i.paired_at && (!u.first || i.paired_at < u.first)) u.first = i.paired_at;
  });
  const kids = {};
  Object.values(users).forEach(u => { (kids[u.parent] = kids[u.parent] || []).push(u); });
  const pending = installs.filter(i => !i.user_id);
  const nPf = (pf) => installs.filter(i => i.user_id && i.platform === pf).length;
  const sum = (k) => links.reduce((a, l) => a + Number(l[k] || 0), 0);

  const copy = (url) => { try { navigator.clipboard.writeText(url); setMsg('📋 Скопійовано: ' + url); } catch (e) { setMsg(url); } };
  const makeUserLink = () => {
    const id = parseInt(uid, 10);
    if (!id) return;
    api.post('/app-invites/link', { kind: 'user', id }).then(r => { setMsg('✅ Посилання юзера #' + id + ': ' + r.data.url); setUid(''); setRev(x => x + 1); })
      .catch(() => setMsg('❌ Юзера #' + id + ' не знайдено'));
  };

  const UserCard = ({ u }) => (
    <Grid item xs={12} sm={6} md={4}>
      <Card data-app-user={u.user_id} sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2, height: '100%' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography onClick={() => navigate('/users/' + u.user_id)}
            sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline dotted', mb: 0.8, wordBreak: 'break-all' }}>
            {uname(u)}</Typography>
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 0.8 }}>
            {Object.keys(u.pf).map(p => <Chip key={p} size="small" label={PF[p].t} sx={chipSx(PF[p].c)}/>)}
            <Chip size="small" label={(METHOD[u.method] || METHOD.none).t} sx={chipSx((METHOD[u.method] || METHOD.none).c)}/>
          </Box>
          <Typography variant="caption" sx={{ color: '#9a9ab0', display: 'block' }}>✅ підключив {kyiv(u.first)}{u.partner_name ? ' · партнер «' + u.partner_name + '»' : ''}</Typography>
          {(kids['user:' + u.user_id] || []).length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 'bold' }}>➡️ привів:</Typography>
              {kids['user:' + u.user_id].map(k => <Chip key={k.user_id} size="small" label={uname(k)} onClick={() => navigate('/users/' + k.user_id)}
                sx={{ ...chipSx('#a78bfa'), cursor: 'pointer' }}/>)}
            </Box>)}
        </CardContent>
      </Card>
    </Grid>
  );

  const LinkStats = ({ l }) => !l ? null : (
    <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
      <Chip size="small" label={'👀 ' + l.opens} sx={chipSx('#9a86ff')}/>
      <Chip size="small" label={'📱 ' + l.android} sx={chipSx(PF.phone.c)}/>
      <Chip size="small" label={'💻 ' + l.pc} sx={chipSx(PF.pc.c)}/>
    </Box>
  );

  const Group = ({ k, title, color, link }) => {
    const list = (kids[k] || []).slice().sort((a, b) => String(b.first || '').localeCompare(String(a.first || '')));
    return (
      <Paper data-app-group={k} sx={{ bgcolor: color + '0a', border: '1px solid ' + color + '55', borderRadius: 2, p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: list.length ? 1.5 : 0 }}>
          <Typography sx={{ color, fontWeight: 'bold', fontSize: 16, mr: 'auto' }}>{title} · привели: {list.length}</Typography>
          <LinkStats l={link}/>
          {link && <Button size="small" onClick={() => copy(link.url)} sx={{ textTransform: 'none', color }}>📋 посилання</Button>}
        </Box>
        {list.length > 0 && <Grid container spacing={1.5}>{list.map(u => <UserCard key={u.user_id} u={u}/>)}</Grid>}
      </Paper>
    );
  };

  const ownerLink = links.find(l => l.kind === 'owner');
  const partnerKeys = Array.from(new Set(links.filter(l => l.kind === 'partner').map(l => 'partner:' + l.ref_id)
    .concat(Object.keys(kids).filter(k => k.startsWith('partner:')))));
  const pLink = (k) => links.find(l => l.kind === 'partner' && 'partner:' + l.ref_id === k);
  const pName = (k) => (pLink(k) || {}).name || (Object.values(users).find(u => 'partner:' + u.partner_id === k) || {}).partner_name || k;

  const feed = events.map(e => ({ at: e.created_at, t: EV[e.ev] || e.ev, who: linkName(byCode[e.code]) }))
    .concat(installs.map(i => ({ at: i.first_run, t: (PF[i.platform] || PF.pc).t + ' · 🚀 перший запуск', who: i.code ? linkName(byCode[i.code]) : '' })))
    .concat(installs.filter(i => i.paired_at).map(i => ({ at: i.paired_at, t: (PF[i.platform] || PF.pc).t + ' · ✅ підключив ' + uname(i), who: '', uid: i.user_id })))
    .sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))).slice(0, 150);

  const tabs = [['tree', '🌳 Хто кого привів'], ['feed', '🕐 Події'], ['links', '🔗 Посилання']];
  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>📲 Хто кого привів у застосунки</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip label={'👀 відкрили посилання · ' + sum('opens')} sx={chipSx('#9a86ff')}/>
        <Chip label={'📥 натиснули 📱 ' + sum('android') + ' · 💻 ' + sum('pc')} sx={chipSx('#f59e0b')}/>
        <Chip label={'🚀 перший запуск · ' + installs.length} sx={chipSx('#a78bfa')}/>
        <Chip label={'✅ підключили 📱 ' + nPf('phone') + ' · 💻 ' + nPf('pc')} sx={chipSx('#4caf50')}/>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => (
          <Button key={k} data-atab={k} size="small" onClick={() => setTab(k)} variant={tab === k ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', borderRadius: 5, px: 2, fontWeight: 'bold',
                  bgcolor: tab === k ? 'var(--ac1, #7c4dff)' : 'transparent', color: tab === k ? '#fff' : 'var(--ac2, #a78bfa)',
                  borderColor: 'var(--ac1, #7c4dff)' }}>{l}</Button>
        ))}
      </Box>
      {msg && <Typography onClick={() => setMsg('')} sx={{ color: '#7ffcd3', mb: 2, wordBreak: 'break-all', cursor: 'pointer' }}>{msg}</Typography>}

      {tab === 'tree' && (<>
        <Group k="owner" title="👑 Ви (ваше посилання)" color="#facc15" link={ownerLink}/>
        {partnerKeys.map(k => <Group key={k} k={k} title={'🤝 партнер «' + pName(k) + '»'} color="#4fc3f7" link={pLink(k)}/>)}
        {(kids.none || []).length > 0 && <Group k="none" title="❔ Без посилання і без партнера" color="#9e9e9e"/>}
        {pending.length > 0 && (
          <Paper sx={{ bgcolor: '#a78bfa0a', border: '1px solid #a78bfa55', borderRadius: 2, p: { xs: 1.5, sm: 2 }, mb: 2 }}>
            <Typography sx={{ color: '#a78bfa', fontWeight: 'bold', fontSize: 16, mb: 1.5 }}>⏳ Запустили, але ще не підключили · {pending.length}</Typography>
            <Grid container spacing={1.5}>
              {pending.slice(0, 60).map((i, n) => (
                <Grid item xs={12} sm={6} md={4} key={n}>
                  <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2 }}><CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Chip size="small" label={(PF[i.platform] || PF.pc).t} sx={{ ...chipSx((PF[i.platform] || PF.pc).c), mb: 0.8 }}/>
                    <Typography variant="caption" sx={{ color: '#9a9ab0', display: 'block' }}>🚀 {kyiv(i.first_run)} · {i.code ? 'посилання: ' + linkName(byCode[i.code]) : 'посилання не знайдено'}</Typography>
                  </CardContent></Card>
                </Grid>))}
            </Grid>
          </Paper>)}
        {installs.length === 0 && <Typography sx={{ color: '#888', textAlign: 'center', mt: 2 }}>😴 Ще ніхто не встановив застосунок за посиланням. Скопіюйте посилання у вкладці «🔗 Посилання» і дайте партнерам.</Typography>}
      </>)}

      {tab === 'feed' && (feed.length === 0
        ? <Typography sx={{ color: '#888', textAlign: 'center', mt: 2 }}>😴 Подій ще немає</Typography>
        : <Grid container spacing={1.5}>
            {feed.map((f, n) => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2, cursor: f.uid ? 'pointer' : 'default' }} onClick={() => f.uid && navigate('/users/' + f.uid)}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" sx={{ color: '#9a9ab0' }}>{kyiv(f.at)}</Typography>
                    <Typography sx={{ color: '#fff', fontSize: 14 }}>{f.t}</Typography>
                    {f.who && <Typography variant="caption" sx={{ color: '#a78bfa' }}>посилання: {f.who}</Typography>}
                  </CardContent>
                </Card>
              </Grid>))}
          </Grid>)}

      {tab === 'links' && (<>
        <Typography sx={{ color: '#9a9ab0', mb: 2, fontSize: 14 }}>
          Дайте кожному партнеру ЙОГО посилання - тоді тут буде видно, кого привів саме він. Людина відкриває посилання, качає застосунок
          для телефона або ПК, а при першому запуску підключає його через бота - вам приходить сповіщення.</Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {links.map(l => (
            <Grid item xs={12} sm={6} md={4} key={l.code}>
              <Card data-app-link={l.kind} sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography sx={{ color: '#fff', fontWeight: 'bold', mb: 0.5 }}>{linkName(l)}</Typography>
                  <Typography variant="caption" sx={{ color: '#7ffcd3', display: 'block', mb: 1, wordBreak: 'break-all' }}>{l.url}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <LinkStats l={l}/>
                    <Button size="small" variant="outlined" onClick={() => copy(l.url)} sx={{ textTransform: 'none', ml: 'auto' }}>📋 Копіювати</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>))}
        </Grid>
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2, p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ color: '#ccc', fontSize: 14 }}>👤 Посилання для окремого юзера (ID з «Користувачі»):</Typography>
          <TextField size="small" value={uid} onChange={e => setUid(e.target.value.replace(/\D/g, ''))} placeholder="ID"
            sx={{ width: 110, input: { color: '#fff' } }}/>
          <Button size="small" variant="contained" onClick={makeUserLink} disabled={!uid}>Створити</Button>
        </Paper>
      </>)}
    </Box>
  );
}
